import type { ColumnDefinition } from "/@lib/types/Table"
import type {
  Column,
  ColumnOrderState,
  ColumnSizingState,
  SortingState,
} from "@tanstack/table-core"

export const MIN_RESIZE_WIDTH = 12

export const TABLE_STORAGE_KEYS = {
  positions: "tradedesk:positions:column-widths",
  positionsGroupBySymbol: "tradedesk:positions:group-by-symbol",
  customers: "tradedesk:customers:column-widths",
  orders: "tradedesk:orders:column-widths",
  orderLegs: "tradedesk:order-legs:column-widths",
  notes: "tradedesk:notes:column-widths",
  pendingCash: "tradedesk:pending-cash:column-widths",
} as const

export type PersistedLayout = {
  columnSizing: ColumnSizingState
  columnOrder: ColumnOrderState
  sorting: SortingState
}

export function loadPersisted(
  key: string | undefined
): Partial<PersistedLayout> {
  if (!key) return {}
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Partial<PersistedLayout>
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch {
    return {}
  }
}

export function reconcileColumnOrder(
  stored: string[] | undefined,
  defined: string[]
): string[] {
  if (!stored || stored.length === 0) return defined
  const definedSet = new Set(defined)
  const seen = new Set<string>()
  const reconciled: string[] = []
  for (const name of stored) {
    if (definedSet.has(name) && !seen.has(name)) {
      reconciled.push(name)
      seen.add(name)
    }
  }
  for (const name of defined) {
    if (!seen.has(name)) reconciled.push(name)
  }
  return reconciled
}

// A Range on the cell returns the flex-allocated width of inner
// buttons, not the natural text — copy text + CSS into a ghost span.
export function naturalWidthOf(cell: HTMLElement): number {
  const text = (cell.textContent ?? "").trim()
  if (!text) return 0
  const cs = getComputedStyle(cell)
  const ghost = document.createElement("span")
  ghost.style.cssText =
    "visibility: hidden; position: absolute; white-space: nowrap; left: -9999px; top: -9999px"
  ghost.style.font = cs.font
  ghost.style.textTransform = cs.textTransform
  ghost.style.letterSpacing = cs.letterSpacing
  ghost.textContent = text
  document.body.appendChild(ghost)
  const textWidth = ghost.getBoundingClientRect().width
  ghost.remove()
  const padL = parseFloat(cs.paddingLeft) || 0
  const padR = parseFloat(cs.paddingRight) || 0
  return Math.ceil(textWidth + padL + padR)
}

export function autofitColumn<T>(opts: {
  rootElement: HTMLElement
  visibleColumns: Pick<Column<T>, "id">[]
  columns: ColumnDefinition[]
  items: T[]
  columnId: string
  groupedFirstColumnIndentRem?: number
}): number | null {
  const { rootElement: root, visibleColumns, columns, items, columnId } = opts
  const domIdx = visibleColumns.findIndex((c) => c.id === columnId)
  if (domIdx === -1) return null
  const colDef = columns.find((c) => c.name === columnId)
  if (!colDef) return null

  let maxWidth = 0
  const headerCells = root.querySelectorAll("[role='columnheader']")
  const headerCell = headerCells[domIdx] as HTMLElement | undefined
  if (headerCell) maxWidth = Math.max(maxWidth, naturalWidthOf(headerCell))

  const sampleCell = root.querySelector("[role='cell']") as HTMLElement | null
  if (sampleCell) {
    const cs = getComputedStyle(sampleCell)
    const padR = parseFloat(cs.paddingRight) || 0
    const indentRem = opts.groupedFirstColumnIndentRem ?? 0
    const padL =
      indentRem > 0 && domIdx === 0
        ? indentRem *
          (parseFloat(getComputedStyle(document.documentElement).fontSize) ||
            16)
        : parseFloat(cs.paddingLeft) || 0
    const ghost = document.createElement("span")
    ghost.style.cssText =
      "visibility: hidden; position: absolute; white-space: nowrap; left: -9999px; top: -9999px"
    ghost.style.font = cs.font
    document.body.appendChild(ghost)
    for (const item of items) {
      const value = colDef.dataFormatter(item)
      const text = value == null ? "" : String(value)
      if (!text) continue
      ghost.textContent = text
      maxWidth = Math.max(
        maxWidth,
        Math.ceil(ghost.getBoundingClientRect().width + padL + padR)
      )
    }
    ghost.remove()
  }
  if (maxWidth === 0) return null
  return Math.max(MIN_RESIZE_WIDTH, maxWidth)
}
